import * as React from 'react'

import { type NonFsdsTheme,resolveNonFsdsTheme } from '@/lib/non-fsds-theme'

import type { NonFsdsFeatureItem } from '@/components/homepage/non-fsds-landing-content'

interface NonFsdsFeaturePoweredSectionProps {
  title: string
  description: string
  items?: NonFsdsFeatureItem[]
  theme?: NonFsdsTheme | string
}

export default function NonFsdsFeaturePoweredSection({ title, description, items = [], theme = 'general' }: NonFsdsFeaturePoweredSectionProps) {
  const themeStyles = {
    general: { cardBorder: 'border-blue-100/80', cardShadow: 'hover:shadow-blue-100/40', numberBg: 'from-blue-50 to-blue-100 text-blue-700 ring-blue-100/50', bulletBg: 'bg-blue-500', hoverGradient: 'from-blue-400 via-sky-400 to-blue-300' },
    'english-academy': { cardBorder: 'border-blue-100/80', cardShadow: 'hover:shadow-emerald-100/40', numberBg: 'from-blue-50 to-emerald-100 text-blue-700 ring-emerald-100/50', bulletBg: 'bg-blue-500', hoverGradient: 'from-blue-400 via-emerald-400 to-blue-300' },
    programming: { cardBorder: 'border-indigo-100/80', cardShadow: 'hover:shadow-indigo-100/40', numberBg: 'from-indigo-50 to-indigo-100 text-indigo-700 ring-indigo-100/50', bulletBg: 'bg-indigo-500', hoverGradient: 'from-indigo-400 via-blue-400 to-indigo-300' },
    math: { cardBorder: 'border-teal-100/80', cardShadow: 'hover:shadow-teal-100/40', numberBg: 'from-teal-50 to-teal-100 text-teal-700 ring-teal-100/50', bulletBg: 'bg-teal-500', hoverGradient: 'from-teal-400 via-sky-400 to-teal-300' },
  }[resolveNonFsdsTheme(theme)]

  return (
    <section data-testid="non-fsds-feature-powered-section" className="bg-white px-4 py-12 sm:px-6 sm:py-16 md:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl space-y-12 sm:space-y-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-[2.75rem] lg:leading-tight">{title}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-slate-600">{description}</p>
        </div>
        {items.length > 0 && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
            {items.map((feature, index) => (
              <article key={index} className={`group relative flex flex-col overflow-hidden rounded-[24px] border bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${themeStyles.cardBorder} ${themeStyles.cardShadow}`}>
                <div className="p-6 pb-4 sm:p-8 sm:pb-5">
                  <div className="flex items-start gap-4">
                    <span className={`inline-flex min-h-12 min-w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-lg font-bold shadow-inner ring-1 ${themeStyles.numberBg}`}>{index + 1}</span>
                    <div className="min-w-0 flex-1 pt-1">
                      <h3 className="text-xl font-bold leading-snug text-slate-900">{feature.title}</h3>
                      <p className="mt-1 flex items-center flex-wrap gap-2 text-sm font-medium text-slate-500">
                        {feature.subtitle}
                        {feature.isComingSoon && (<span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-bold text-blue-700">Coming soon</span>)}
                      </p>
                    </div>
                  </div>
                </div>
                {feature.bullets.length > 0 && (
                  <div className="px-6 sm:px-8 pb-8 flex-1">
                    <ul className="space-y-3">
                      {feature.bullets.map((bullet, bulletIndex) => (
                        <li key={bulletIndex} className="flex items-start text-sm sm:text-base text-slate-700">
                          <span className={`mr-3 mt-2 flex size-1.5 shrink-0 rounded-full ${themeStyles.bulletBg}`} />
                          <span className="leading-relaxed">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className={`mt-auto h-2 bg-gradient-to-r opacity-0 transition-opacity duration-300 group-hover:opacity-100 ${themeStyles.hoverGradient}`} />
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
